/**
 * Pinecone injects route HTML without reliably executing inline <script> tags.
 * Wire the player registration form after each navigation.
 */
import { buildPersonaAvatarChoices, newPersonaSeed, personaAvatarUrl } from './players/player-avatar-bank.js';
import { namesFromRandomPick } from '../core/random-player-names.js';

const REGISTER_API = '/api/auth/player-register';

function registerErrorMessage(json, status) {
  const code = String(json?.error || '').trim();
  if (json?.message) return String(json.message);
  if (code === 'email_already_registered') return 'That email is already registered. Sign in instead.';
  if (code === 'valid_email_required') return 'Enter a valid email address.';
  if (code === 'auth_store_unavailable') {
    return 'Registration database is unavailable. Start MongoDB, then restart cloud-api.';
  }
  if (status === 503) return 'Registration service is temporarily unavailable.';
  return code || 'register_failed';
}

function applyAuthSession(token, email, json) {
  const Auth = globalThis.AdminAuth;
  if (Auth?.setToken) Auth.setToken(token);
  else {
    try {
      globalThis.localStorage?.setItem('dartsos.admin.token', token);
    } catch {
      /* ignore */
    }
  }
  const pl = json?.player && typeof json.player === 'object' ? json.player : null;
  const u = json?.user && typeof json.user === 'object' ? json.user : {};
  const em = String(pl?.email || u.email || email || '').trim();
  const role = String(u.role || pl?.role || 'player').trim().toLowerCase() || 'player';
  const accountId = String(u.accountId || pl?.accountId || '').trim();
  const playerIdRaw = u.playerId ?? pl?.playerId;
  const playerId = playerIdRaw != null && String(playerIdRaw).trim() ? String(playerIdRaw).trim() : null;
  const me = {
    _id: em,
    email: em,
    role,
    fullName: String(pl?.fullName || pl?.displayName || '').trim(),
    displayName: String(pl?.displayName || pl?.fullName || '').trim(),
    imageUrl: String(pl?.imageUrl || '').trim(),
  };
  if (accountId) me.accountId = accountId;
  if (playerId) me.playerId = playerId;
  if (pl?.personId) me.personId = String(pl.personId).trim();
  const payload = { token, me };
  try {
    globalThis.localStorage?.setItem('player.session', JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  try {
    globalThis.Alpine?.store?.('session')?.set?.(payload);
  } catch {
    /* ignore */
  }
}

function bindAdminRegisterFormOnce() {
  const form = document.getElementById('adminRegisterForm');
  if (!form || form.dataset.pdlBound === '1') return;
  form.dataset.pdlBound = '1';

  const errEl = document.getElementById('adminRegisterErr');
  const avatarImg = document.getElementById('regAvatarPreview');
  const suggestBtn = document.getElementById('regSuggestName');
  const shuffleBtn = document.getElementById('regShuffleAvatar');
  let avatarSeed = newPersonaSeed();

  const syncAvatarPreview = () => {
    if (avatarImg) avatarImg.src = personaAvatarUrl(avatarSeed);
  };
  syncAvatarPreview();

  suggestBtn?.addEventListener('click', () => {
    const { fullName, nickname } = namesFromRandomPick();
    const fullEl = document.getElementById('regFullName');
    const nickEl = document.getElementById('regNickname');
    if (fullEl) fullEl.value = fullName;
    if (nickEl) nickEl.value = nickname;
  });

  shuffleBtn?.addEventListener('click', () => {
    const choices = buildPersonaAvatarChoices(avatarSeed);
    const next = choices[Math.floor(Math.random() * choices.length)] || choices[0];
    avatarSeed = next?.seed || newPersonaSeed();
    syncAvatarPreview();
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (errEl) errEl.textContent = '';
    const email = String(document.getElementById('regEmail')?.value || '').trim();
    const fullName = String(document.getElementById('regFullName')?.value || '').trim();
    const nickname = String(document.getElementById('regNickname')?.value || '').trim();
    const country = String(document.getElementById('regCountry')?.value || 'GB').trim();
    const password = String(document.getElementById('regPassword')?.value || '');
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;

    fetch(REGISTER_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        email,
        fullName,
        nickname,
        country,
        password,
        imageUrl: personaAvatarUrl(avatarSeed),
      }),
    })
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (!res.ok) throw new Error(registerErrorMessage(data, res.status));
        const token = String(data.token || data.accessToken || '').trim();
        if (!token) throw new Error('register_failed');
        applyAuthSession(token, email, data);
        globalThis.location.href = '/admin/dashboard';
      })
      .catch((e) => {
        if (errEl) errEl.textContent = String(e?.message || 'register_failed');
      })
      .finally(() => {
        if (btn) btn.disabled = false;
      });
  });
}

document.addEventListener('pinecone:after', () => {
  queueMicrotask(bindAdminRegisterFormOnce);
});

queueMicrotask(bindAdminRegisterFormOnce);
